import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import api from '../utils/api';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import RatingSystem from '../components/RatingSystem';
import { 
  Building2, Calendar, Shield, Award, CheckCircle, Truck, 
  Settings, Mail, Phone, MapPin, TrendingUp, ToggleLeft, ToggleRight,
  Package, Gauge, Wrench, Target
} from 'lucide-react';
import { toast } from 'sonner';

const DispatcherProfile = () => {
  const { id } = useParams();
  const [dispatcher, setDispatcher] = useState(null);
  const [loading, setLoading] = useState(true);
  const [ratingsEnabled, setRatingsEnabled] = useState(true);

  useEffect(() => {
    fetchDispatcher();
  }, [id]);

  const fetchDispatcher = async () => {
    try {
      // If no ID provided, fetch the first dispatcher
      const response = id 
        ? await api.get(`/dispatchers/${id}`)
        : await api.get('/dispatchers/list');
      
      const dispatcherData = id ? response.data : response.data[0];
      setDispatcher(dispatcherData);
      setRatingsEnabled(dispatcherData.ratings_enabled !== false);
    } catch (error) {
      toast.error('Failed to load dispatcher profile');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleRatings = async () => {
    try {
      const response = await api.put(`/dispatchers/${dispatcher.id}/toggle-ratings`);
      setRatingsEnabled(response.data.ratings_enabled);
      toast.success(`Ratings ${response.data.ratings_enabled ? 'enabled' : 'disabled'}`);
    } catch (error) {
      toast.error('Failed to toggle ratings');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-blue-500 border-r-transparent"></div>
          <p className="mt-4 text-slate-600 font-medium">Loading dispatcher profile...</p>
        </div>
      </div>
    );
  }

  if (!dispatcher) {
    return (
      <div className="min-h-screen flex flex-col bg-slate-50">
        <Navbar />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <Building2 className="h-16 w-16 text-slate-300 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-slate-900 mb-2">Dispatcher Not Found</h2>
            <p className="text-slate-600">The dispatcher profile you're looking for doesn't exist.</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <Navbar />

      <main className="flex-1 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 w-full">
        {/* Header Section */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-r from-blue-600 to-blue-500 text-white rounded-md p-8 mb-8 shadow-lg"
        >
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
              <div className="bg-white/20 p-4 rounded-md backdrop-blur-sm">
                <Building2 className="h-12 w-12 text-white" />
              </div>
              <div>
                <h1 className="font-heading text-3xl sm:text-4xl font-bold mb-2" data-testid="dispatcher-name">
                  {dispatcher.company_name}
                </h1>
                <p className="text-blue-100 text-lg">
                  Verified StraightConnect Dispatch Partner
                </p>
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2 bg-white/20 px-4 py-2 rounded-md backdrop-blur-sm">
                <Shield className="h-5 w-5" />
                <span className="font-semibold">Verified</span>
              </div>
              <button
                onClick={handleToggleRatings}
                className="flex items-center gap-2 bg-white/20 px-4 py-2 rounded-md backdrop-blur-sm hover:bg-white/30 transition-all"
                data-testid="toggle-ratings-btn"
              >
                {ratingsEnabled ? (
                  <ToggleRight className="h-5 w-5" />
                ) : (
                  <ToggleLeft className="h-5 w-5" />
                )}
                <span className="text-sm font-medium">
                  Ratings {ratingsEnabled ? 'On' : 'Off'}
                </span>
              </button>
            </div>
          </div>
        </motion.div>

        {/* About Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white border border-slate-200 rounded-md p-6 mb-6 shadow-sm"
        >
          <h2 className="font-heading text-2xl font-bold text-slate-900 mb-4">About Us</h2>
          <p className="text-slate-700 leading-relaxed mb-6">{dispatcher.description}</p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-md">
              <Calendar className="h-5 w-5 text-blue-500" />
              <div>
                <p className="text-sm text-slate-600">Established</p>
                <p className="font-semibold text-slate-900">{dispatcher.established}</p>
              </div>
            </div>
            <div className="p-4 bg-slate-50 rounded-md">
              <p className="text-sm text-slate-600 mb-1">Dispatch Type</p>
              <p className="font-semibold text-slate-900 text-sm">{dispatcher.dispatch_type}</p>
            </div>
            <div className="p-4 bg-slate-50 rounded-md">
              <p className="text-sm text-slate-600 mb-1">Operating Authority</p>
              <p className="font-semibold text-slate-900 text-sm">{dispatcher.operating_authority}</p>
            </div>
            <div className="p-4 bg-slate-50 rounded-md">
              <p className="text-sm text-slate-600 mb-1">Contact</p>
              <div className="space-y-1">
                {dispatcher.contact_phone && (
                  <p className="text-xs text-slate-700 flex items-center gap-1">
                    <Phone className="h-3 w-3" />
                    {dispatcher.contact_phone}
                  </p>
                )}
                {dispatcher.contact_email && (
                  <p className="text-xs text-slate-700 flex items-center gap-1">
                    <Mail className="h-3 w-3" />
                    {dispatcher.contact_email}
                  </p>
                )}
              </div>
            </div>
          </div>
        </motion.div>

        {/* Certifications & Core Values */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white border border-slate-200 rounded-md p-6 shadow-sm"
          >
            <div className="flex items-center gap-2 mb-4">
              <Award className="h-6 w-6 text-blue-500" />
              <h2 className="font-heading text-xl font-bold text-slate-900">Certifications</h2>
            </div>
            <div className="space-y-2">
              {dispatcher.certifications.map((cert) => (
                <div key={cert} className="flex items-center gap-2 p-3 bg-green-50 rounded-md">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                  <span className="font-medium text-slate-900">{cert}</span>
                </div>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white border border-slate-200 rounded-md p-6 shadow-sm"
          >
            <div className="flex items-center gap-2 mb-4">
              <TrendingUp className="h-6 w-6 text-blue-500" />
              <h2 className="font-heading text-xl font-bold text-slate-900">Core Values</h2>
            </div>
            <div className="flex flex-wrap gap-3">
              {dispatcher.core_values.map((value) => (
                <span
                  key={value}
                  className="inline-block px-4 py-2 bg-blue-100 text-blue-700 rounded-full font-medium"
                >
                  {value}
                </span>
              ))}
            </div>
          </motion.div>
        </div>

        {/* Services Offered */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white border border-slate-200 rounded-md p-6 mb-6 shadow-sm"
        >
          <h2 className="font-heading text-2xl font-bold text-slate-900 mb-4">🚀 Services Offered</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {dispatcher.services.map((service) => (
              <div key={service} className="flex items-start gap-3 p-3 border border-slate-200 rounded-md hover:border-blue-300 transition-colors">
                <CheckCircle className="h-5 w-5 text-blue-500 mt-0.5 flex-shrink-0" />
                <span className="text-slate-700">{service}</span>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Fleet Partnerships */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white border border-slate-200 rounded-md p-6 mb-6 shadow-sm"
        >
          <div className="flex items-center gap-2 mb-4">
            <Truck className="h-6 w-6 text-blue-500" />
            <h2 className="font-heading text-2xl font-bold text-slate-900">🤝 Fleet Partnerships</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-slate-900">Carrier</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-slate-900">Equipment</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-slate-900">Region</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-slate-900">Relationship</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {dispatcher.fleet_partnerships.map((partnership) => (
                  <tr key={partnership.carrier} className="hover:bg-slate-50 transition-colors">
                    <td className="px-4 py-3 font-medium text-slate-900">{partnership.carrier}</td>
                    <td className="px-4 py-3 text-slate-700">{partnership.equipment}</td>
                    <td className="px-4 py-3 text-slate-700">{partnership.region}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${
                        partnership.relationship === 'Dedicated' ? 'bg-green-100 text-green-800' :
                        partnership.relationship === 'Contract' ? 'bg-blue-100 text-blue-800' :
                        'bg-purple-100 text-purple-800'
                      }`}>
                        {partnership.relationship}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>

        {/* Technology & Tools */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-white border border-slate-200 rounded-md p-6 shadow-sm mb-8"
        >
          <div className="flex items-center gap-2 mb-4">
            <Settings className="h-6 w-6 text-blue-500" />
            <h2 className="font-heading text-2xl font-bold text-slate-900">🛠️ Technology & Tools</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
            {dispatcher.technology_tools.map((tool) => (
              <div
                key={tool}
                className="flex items-center gap-2 p-3 bg-slate-50 border border-slate-200 rounded-md"
              >
                <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                <span className="text-slate-700 text-sm">{tool}</span>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Equipment & Capabilities */}
        {dispatcher.equipment_capabilities && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="bg-white border border-slate-200 rounded-md p-6 shadow-sm mb-8"
          >
            <div className="flex items-center gap-2 mb-4">
              <Package className="h-6 w-6 text-blue-500" />
              <h2 className="font-heading text-2xl font-bold text-slate-900">📦 Equipment & Capabilities</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <p className="text-sm font-semibold text-slate-600 mb-2">Equipment Type</p>
                  <div className="flex items-center gap-2 p-4 bg-blue-50 rounded-md border border-blue-200">
                    <Truck className="h-5 w-5 text-blue-600" />
                    <span className="font-medium text-blue-900">{dispatcher.equipment_capabilities.equipment_type}</span>
                  </div>
                </div>
                <div>
                  <p className="text-sm font-semibold text-slate-600 mb-2">Weight Capacity</p>
                  <div className="flex items-center gap-2 p-4 bg-green-50 rounded-md border border-green-200">
                    <Gauge className="h-5 w-5 text-green-600" />
                    <span className="font-medium text-green-900">{dispatcher.equipment_capabilities.weight_capacity}</span>
                  </div>
                </div>
              </div>
              <div className="space-y-4">
                <div>
                  <p className="text-sm font-semibold text-slate-600 mb-2">Features</p>
                  <div className="space-y-2">
                    {dispatcher.equipment_capabilities.features.map((feature) => (
                      <div key={feature} className="flex items-center gap-2 p-2 bg-slate-50 rounded-md">
                        <CheckCircle className="h-4 w-4 text-blue-500" />
                        <span className="text-slate-700">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="text-sm font-semibold text-slate-600 mb-2">Service Area</p>
                  <div className="flex items-center gap-2 p-4 bg-purple-50 rounded-md border border-purple-200">
                    <MapPin className="h-5 w-5 text-purple-600" />
                    <span className="font-medium text-purple-900">{dispatcher.equipment_capabilities.service_area}</span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Performance Metrics */}
        {dispatcher.performance_metrics && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
            className="bg-white border border-slate-200 rounded-md p-6 shadow-sm mb-8"
          >
            <div className="flex items-center gap-2 mb-4">
              <Target className="h-6 w-6 text-blue-500" />
              <h2 className="font-heading text-2xl font-bold text-slate-900">📊 Performance Metrics</h2>
            </div>
            <div className="space-y-4">
              <div className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-md border border-green-200">
                <div className="flex items-start gap-3">
                  <div className="bg-green-500 rounded-full p-2 mt-1">
                    <CheckCircle className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-green-900 mb-1">On-Time Performance</p>
                    <p className="text-green-800">{dispatcher.performance_metrics.on_time_performance}</p>
                  </div>
                </div>
              </div>
              <div className="p-4 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-md border border-blue-200">
                <div className="flex items-start gap-3">
                  <div className="bg-blue-500 rounded-full p-2 mt-1">
                    <Truck className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-blue-900 mb-1">Load Volume</p>
                    <p className="text-blue-800">{dispatcher.performance_metrics.load_volume}</p>
                  </div>
                </div>
              </div>
              <div className="p-4 bg-gradient-to-r from-purple-50 to-violet-50 rounded-md border border-purple-200">
                <div className="flex items-start gap-3">
                  <div className="bg-purple-500 rounded-full p-2 mt-1">
                    <Phone className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-purple-900 mb-1">Communication</p>
                    <p className="text-purple-800">{dispatcher.performance_metrics.communication}</p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Core Skills & Software Proficiency */}
        {dispatcher.core_skills && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
            className="bg-white border border-slate-200 rounded-md p-6 shadow-sm mb-8"
          >
            <div className="flex items-center gap-2 mb-4">
              <Wrench className="h-6 w-6 text-blue-500" />
              <h2 className="font-heading text-2xl font-bold text-slate-900">💼 Core Skills & Software Proficiency</h2>
            </div>
            <div className="space-y-6">
              {dispatcher.core_skills.load_boards && (
                <div>
                  <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                    <div className="w-1 h-6 bg-blue-500 rounded"></div>
                    Load Boards
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {dispatcher.core_skills.load_boards.map((board) => (
                      <span
                        key={board}
                        className="inline-block px-4 py-2 bg-blue-100 text-blue-800 rounded-full font-medium text-sm"
                      >
                        {board}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {dispatcher.core_skills.compliance && (
                <div>
                  <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                    <div className="w-1 h-6 bg-green-500 rounded"></div>
                    Compliance
                  </h3>
                  <div className="space-y-2">
                    {dispatcher.core_skills.compliance.map((skill) => (
                      <div key={skill} className="flex items-center gap-2 p-3 bg-green-50 rounded-md border border-green-200">
                        <CheckCircle className="h-4 w-4 text-green-600" />
                        <span className="text-green-900">{skill}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {dispatcher.core_skills.back_office && (
                <div>
                  <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                    <div className="w-1 h-6 bg-purple-500 rounded"></div>
                    Back Office
                  </h3>
                  <div className="space-y-2">
                    {dispatcher.core_skills.back_office.map((skill) => (
                      <div key={skill} className="flex items-center gap-2 p-3 bg-purple-50 rounded-md border border-purple-200">
                        <CheckCircle className="h-4 w-4 text-purple-600" />
                        <span className="text-purple-900">{skill}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        )}

        {/* Rating System */}
        {ratingsEnabled && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9 }}
          >
            <RatingSystem 
              targetId={dispatcher.id}
              targetType="dispatcher"
              targetName={dispatcher.company_name}
            />
          </motion.div>
        )}

        {!ratingsEnabled && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9 }}
            className="bg-slate-50 border border-slate-200 rounded-md p-8 text-center"
          >
            <Shield className="h-12 w-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-600">This profile has disabled ratings and reviews.</p>
          </motion.div>
        )}

        {/* Contact CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-md p-8 mt-8 shadow-lg text-center"
        >
          <h2 className="font-heading text-2xl font-bold mb-2">Ready to Partner with Us?</h2>
          <p className="text-orange-100 mb-6">
            Join our network of verified carriers and start maximizing your revenue today.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            {dispatcher.contact_email && (
              <a
                href={`mailto:${dispatcher.contact_email}`}
                className="bg-white text-orange-600 px-8 py-3 rounded-md font-semibold hover:bg-orange-50 transition-all btn-hover-lift inline-flex items-center justify-center gap-2"
              >
                <Mail className="h-5 w-5" />
                Email Us
              </a>
            )}
            {dispatcher.contact_phone && (
              <a
                href={`tel:${dispatcher.contact_phone}`}
                className="bg-white/10 backdrop-blur-sm text-white border-2 border-white px-8 py-3 rounded-md font-semibold hover:bg-white/20 transition-all inline-flex items-center justify-center gap-2"
              >
                <Phone className="h-5 w-5" />
                Call Now
              </a>
            )}
          </div>
        </motion.div>
      </main>

      <Footer />
    </div>
  );
};

export default DispatcherProfile;
